import { useParams } from 'react-router-dom'
import { useContext, useEffect, useRef, useState } from 'react'
import { APIContext } from 'context/APIContext'
import { DocumentDuplicateIcon, MicrophoneIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline'
import { useGetExercises } from 'redux/customHooks/useGetExercises'

import ScreenFrame from 'components/Frame/ScreenFrame'
import ExerciseCollection from 'components/ExerciseCollection/ExerciseCollection'
import ContentFrame from 'components/Frame/ContentFrame'

const SpeechRecognitionAPI = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null

export default function ExerciseCollectionView() {

    const { urlExerciseCollectionId } = useParams()

    const { exerciseHashMap } = useGetExercises()

    const { GetExerciseCollection, GenerateExerciseCollectionFromPrompt } = useContext(APIContext)

    const recognitionRef = useRef(null)
    const shouldKeepListeningRef = useRef(false)

    const [staticExerciseCollectionName, setStaticExerciseCollectionName] = useState('')
    const [exerciseCollection, setExerciseCollection] = useState(null)
    const [isListening, setIsListening] = useState(false)
    const [promptText, setPromptText] = useState('')
    const [promptLoading, setPromptLoading] = useState(false)

    const InitializeExerciseCollection = async () => {
        setExerciseCollection(null)

        if (!urlExerciseCollectionId) {
            return
        }

        const exerciseCollection = await GetExerciseCollection(urlExerciseCollectionId)
        
        setStaticExerciseCollectionName(exerciseCollection?.name ?? '')
        setExerciseCollection(exerciseCollection)
    }

    const StopDictation = () => {
        shouldKeepListeningRef.current = false

        if (recognitionRef.current) {
            try {
                recognitionRef.current.stop()
            } catch (error) {
                // Recognition may already be stopped
            }
        }

        setIsListening(false)
    }

    const OnToggleDictation = () => {
        if (!SpeechRecognitionAPI || promptLoading) {
            return
        }

        if (isListening) {
            StopDictation()
            return
        }

        const recognition = new SpeechRecognitionAPI()
        recognition.continuous = true
        recognition.interimResults = false
        recognition.lang = 'en-US'

        recognition.onresult = (event) => {
            let finalTranscript = ''

            for (let i = event.resultIndex; i < event.results.length; i++) {
                if (event.results[i].isFinal) {
                    finalTranscript += event.results[i][0].transcript
                }
            }

            const trimmedTranscript = finalTranscript.trim()

            if (!trimmedTranscript) {
                return
            }

            setPromptText((prev) => {
                const spacer = prev && !prev.endsWith(' ') && !prev.endsWith('\n') ? ' ' : ''
                return `${prev}${spacer}${trimmedTranscript}`
            })
        }

        recognition.onerror = (event) => {
            console.error('Speech recognition error:', event.error)
            shouldKeepListeningRef.current = false
            setIsListening(false)
        }

        recognition.onend = () => {
            if (shouldKeepListeningRef.current) {
                try {
                    recognition.start()
                    return
                } catch (error) {
                    console.error(error)
                }
            }

            setIsListening(false)
        }

        recognitionRef.current = recognition
        shouldKeepListeningRef.current = true

        try {
            recognition.start()
            setIsListening(true)
        } catch (error) {
            console.error(error)
            shouldKeepListeningRef.current = false
            setIsListening(false)
        }
    }

    const OnGenerateFromPrompt = async () => {
        const trimmedPrompt = promptText.trim()

        if (!trimmedPrompt || promptLoading) {
            return
        }

        StopDictation()
        setPromptLoading(true)

        const generatedExerciseCollectionCycles = await GenerateExerciseCollectionFromPrompt(trimmedPrompt)

        if (generatedExerciseCollectionCycles !== null) {
            setExerciseCollection(prev => ({
                ...prev,
                exerciseCollectionCycles: generatedExerciseCollectionCycles
            }))
        }

        setPromptLoading(false)
    }

    const UpdateExerciseCollectionName = (name) => {
        setExerciseCollection(prev => {
            return { ...prev, name: name }
        })
    }

    const UpdateExerciseCollectionDescription = (description) => {
        setExerciseCollection(prev => {
            return { ...prev, description: description }
        })
    }

    useEffect(() => {
        return () => {
            shouldKeepListeningRef.current = false

            if (recognitionRef.current) {
                try {
                    recognitionRef.current.stop()
                } catch (error) {
                    // Recognition may already be stopped
                }
            }
        }
    }, [])

    useEffect(() => { InitializeExerciseCollection() }, [urlExerciseCollectionId])

    const rawExerciseCollection = exerciseCollection !== null && exerciseCollection !== undefined
        ? JSON.stringify(exerciseCollection)
            .replace(/","/g, '", "')
            .replace(/":/g, '": ')
            .replace(/},/g, '}, ')
            .replace(/],/g, '], ')
        : null

    if (
        Object.keys(exerciseHashMap).length === 0 
        || exerciseCollection === null
    ) {
        return (
            <ScreenFrame>
                <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                <div className='w-full h-[800px] bg-gray-200 rounded-lg animate-pulse' />
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame>
            <div className='flex items-center gap-x-4'>
                <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex-grow">
                    {staticExerciseCollectionName}
                </h1>

                {/* 
                <div className='w-40'>
                    <BrandColorButton
                        size='small'
                        text="Save Changes"
                        className='shadow-md'
                        isLoading={saveLoading}
                    />
                </div> 
                */}
            </div>

            <ContentFrame>
                <div className='w-full p-4 space-y-4'>
                    <div className='space-y-2'>
                        <p className='w-full text-left'>Name</p>
                        <input
                            type="text"
                            className="w-full p-2 border border-gray-300 rounded-md"
                            placeholder="Enter name here"
                            value={exerciseCollection.name}
                            onChange={(e) => UpdateExerciseCollectionName(e.target.value)}
                        />
                    </div>

                    <div className='space-y-2'>
                        <p className='w-full text-left'>Description</p>
                        <textarea
                            className="w-full h-20 p-2 border border-gray-300 rounded-md"
                            placeholder="Enter description here"
                            value={exerciseCollection.description}
                            onChange={(e) => UpdateExerciseCollectionDescription(e.target.value)}
                        />
                    </div>

                    <div className='space-y-2'>
                        <p className='w-full text-left'>Generate with AI</p>

                        <div className='relative rounded-md border border-gray-300 bg-white shadow-sm focus-within:border-gray-400 focus-within:ring-1 focus-within:ring-gray-400'>
                            <textarea
                                className='w-full h-32 max-h-48 resize-none bg-transparent p-4 pr-24 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none'
                                placeholder={isListening
                                    ? 'Listening... speak your exercise collection prompt'
                                    : 'Describe the exercise collection you want to generate...'}
                                value={promptText}
                                onChange={(e) => setPromptText(e.target.value)}
                                disabled={promptLoading}
                            />

                            <div className='absolute bottom-3 right-3 flex items-center gap-x-2'>
                                <button
                                    type='button'
                                    className={`
                                        flex h-9 w-9 items-center justify-center rounded-full
                                        ${!SpeechRecognitionAPI || promptLoading
                                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            : isListening
                                                ? 'bg-red-500 text-white hover:bg-red-600'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
                                    `}
                                    onClick={OnToggleDictation}
                                    disabled={!SpeechRecognitionAPI || promptLoading}
                                    title={SpeechRecognitionAPI
                                        ? (isListening ? 'Stop dictation' : 'Start dictation')
                                        : 'Speech recognition is not supported in this browser'}
                                >
                                    <MicrophoneIcon className={`h-4 w-4 ${isListening ? 'animate-pulse' : ''}`} />
                                </button>

                                <button
                                    type='button'
                                    className={`
                                        flex h-9 w-9 items-center justify-center rounded-full
                                        ${promptText.trim() && !promptLoading
                                            ? 'bg-orange-400 text-white hover:bg-orange-500'
                                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'}
                                    `}
                                    onClick={OnGenerateFromPrompt}
                                    disabled={!promptText.trim() || promptLoading}
                                    title='Generate from prompt'
                                >
                                    {promptLoading ? (
                                        <span className='h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent' />
                                    ) : (
                                        <PaperAirplaneIcon className='h-4 w-4' />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </ContentFrame>

            <ContentFrame>
                <div className='w-full p-4'>
                    <ExerciseCollection
                        exerciseHashMap={exerciseHashMap}
                        programLevelHashMap={{}} // TODO: Add program level hashmap
                        slotIndex={0} // TODO: Add slot index? Less important
                        exerciseCollection={exerciseCollection}
                        setExerciseCollection={setExerciseCollection}
                        allowEditing={true}
                    />
                </div>
            </ContentFrame>

            <div className='grid grid-cols-2 gap-4'>
                <ContentFrame>
                    <pre className='p-4 text-left w-full font-mono text-sm'>
                        {JSON.stringify(exerciseCollection, null, 4)}
                    </pre>
                </ContentFrame>

                <div>
                    <ContentFrame>
                        <div className="w-full relative">
                            <button
                                type="button"
                                className="absolute right-2 top-2 z-10 p-1 rounded-full hover:bg-gray-100"
                                title="Copy JSON to clipboard"
                                onClick={() => navigator.clipboard.writeText(rawExerciseCollection)}
                            >
                                <DocumentDuplicateIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                            </button>

                            <p className="p-4 pr-12 break-words">{rawExerciseCollection}</p>
                        </div>
                    </ContentFrame>
                </div>
            </div>
        </ScreenFrame>
    )
}